import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

interface EmptyStateProps {
  icon?: SvgIconComponent;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  illustration,
  size = 'medium',
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return {
          iconSize: 48,
          titleVariant: 'h6' as const,
          descriptionVariant: 'body2' as const,
          spacing: 2,
          maxWidth: 300,
        };
      case 'large':
        return {
          iconSize: 80,
          titleVariant: 'h4' as const,
          descriptionVariant: 'body1' as const,
          spacing: 4,
          maxWidth: 500,
        };
      default:
        return {
          iconSize: 64,
          titleVariant: 'h5' as const,
          descriptionVariant: 'body1' as const,
          spacing: 3,
          maxWidth: 400,
        };
    }
  };

  const config = getSizeConfig();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: config.spacing * 2,
        px: 2,
        minHeight: size === 'large' ? 400 : size === 'medium' ? 300 : 200,
      }}
    >
      <Stack spacing={config.spacing} alignItems="center" maxWidth={config.maxWidth}>
        {/* Icon or Illustration */}
        {illustration || (Icon && (
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: config.iconSize }} />
          </Box>
        ))}

        {/* Title */}
        <Typography
          variant={config.titleVariant}
          color="text.primary"
          fontWeight="medium"
          sx={{ mb: 1 }}
        >
          {title}
        </Typography>

        {/* Description */}
        {description && (
          <Typography
            variant={config.descriptionVariant}
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {description}
          </Typography>
        )}

        {/* Action Button */}
        {actionLabel && onAction && (
          <Button
            variant="contained"
            onClick={onAction}
            sx={{
              mt: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'medium',
              px: 3,
              py: 1.5,
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
}; 